import * as React from 'react';
import { useThrottleCallback } from '@react-hook/throttle';
import { DesignTokenSizeEnum, styled, useTheme, } from '../theme-provider';
import { getCommonProps } from '../helpers/common-props';
import { getValueByScreenSize, } from '../helpers/breakpoint';
import { ChildContainerTabIndexManager } from './tabindex-manager';
import { GallerySlideContext } from './slide';
import { MediaGalleryContext } from '../media-gallery';
// The index is the index in the range `0 .. length - 1`, it corresponds to the
// number of the slides.
//
// index: 0 1 2 3 4
//
// If there is endless scrolling, slides are duplicated (leading and trailing).
//
// extendedIndex: -2 -1 0 1 2 3 4 5 6
// index:          3  4 0 1 2 3 4 0 1
//
// The number of duplicated slides on each side is one more than the number of
// slides per page, so a complete page and a fading edge can be seen beyond
// either end of the "real" slides.
//
// If there is no endless scrolling, the extendedIndex is the same as the index.
const StyledCarousel = styled.section.withConfig({ displayName: "StyledCarousel", componentId: "sc-1lwge8c" }) `overflow:none;width:100%;position:relative;:focus{outline:none;}`;
const StyledSlides = styled.div.withConfig({ displayName: "StyledSlides", componentId: "sc-17msnuv" }) `display:grid;grid-auto-columns:calc(\n\t\t(\n\t\t\t\t100% -\n\t\t\t\t\t(\n\t\t\t\t\t\t${({ gutter, theme }) => (gutter ? theme.size[gutter] : '0px')} *\n\t\t\t\t\t\t\t${({ slidesPerPage }) => slidesPerPage - 1}\n\t\t\t\t\t)\n\t\t\t) / ${({ slidesPerPage }) => slidesPerPage}\n\t);grid-auto-flow:column;gap:${({ theme, gutter }) => (gutter ? theme.size[gutter] : '0')};${({ disableSnap }) => !disableSnap && `scroll-snap-type: x mandatory;`} overscroll-behavior-x:contain;overflow:auto;::-webkit-scrollbar{display:none;}scrollbar-width:none;:focus{outline:none;box-shadow:0 0 0 ${({ theme }) => theme.size.border.heavy} ${({ theme }) => theme.colors.border.hover};}scroll-padding:0 ${({ fadingEdges, theme }) => (fadingEdges ? theme.size.grid002 : '0')};padding:0 ${({ fadingEdges, theme }) => (fadingEdges ? theme.size.grid002 : '0')};`;
const noop = () => {
    // this function is an intentionally blank default
};
export const GalleryContext = React.createContext({
    index: 0,
    extendedIndex: 0,
    endlessScrolling: false,
    triggerChange: noop,
    triggerNext: noop,
    triggerPrevious: noop,
    getSlideId: () => '',
    disableScrollSnap: noop,
    slidesPerPage: 1,
    fadingEdges: false,
    length: 1,
    id: '',
    accessibleExtendedIndices: [],
    visibleIndices: [],
    snappableExtendedIndices: [],
    slidesRef: undefined,
});
const DEFAULT_MULTI_SLIDE_GUTTER = DesignTokenSizeEnum.grid001;
export const Gallery = (props) => {
    const { slidesPerPage = 1, fadingEdges = false, endlessScrolling, increment = 'slide', handleChange: externalHandleChange, children, id, ariaLabel, ariaLabelledby, topElements, bottomElements, gutter, } = props;
    const theme = useTheme();
    const commonProps = getCommonProps(props);
    const [extendedIndex, setExtendedIndex] = React.useState(0);
    const [currentSlidesPerPage, setCurrentSlidesPerPage] = React.useState(1);
    const [currentFadingEdges, setCurrentFadingEdges] = React.useState(false);
    const [disableSnap, setDisableSnap] = React.useState(false);
    // A consumer-defined gutter always takes effect, but by default we only
    // have a gutter when there is more than one slide per page.
    const currentGutter = React.useMemo(() => gutter !== null && gutter !== void 0 ? gutter : (currentSlidesPerPage > 1 ? DEFAULT_MULTI_SLIDE_GUTTER : undefined), [currentSlidesPerPage, gutter]);
    const slides = React.useRef(null);
    const childrenLength = React.useMemo(() => React.Children.count(children), [children]);
    const updateSlidesPerPage = React.useCallback(() => {
        const width = typeof window !== 'undefined' ? window.innerWidth || 0 : 0;
        const newCurrentSlidesPerPage = getValueByScreenSize(width, slidesPerPage);
        setCurrentSlidesPerPage(newCurrentSlidesPerPage);
    }, [slidesPerPage]);
    const updateFadingEdges = React.useCallback(() => {
        const width = typeof window !== 'undefined' ? window.innerWidth || 0 : 0;
        const fadingEdgesForScreenSize = getValueByScreenSize(width, fadingEdges);
        const newCurrentFadingEdges = fadingEdgesForScreenSize === 'normal' ||
            ((fadingEdgesForScreenSize === 'auto' ||
                fadingEdgesForScreenSize === true) &&
                childrenLength > currentSlidesPerPage);
        setCurrentFadingEdges(newCurrentFadingEdges);
    }, [childrenLength, currentSlidesPerPage, fadingEdges]);
    const changeSlide = React.useCallback((nextIndex, animate = true) => {
        const slidesElement = slides.current;
        if (!slidesElement || slidesElement.children.length <= 1) {
            return;
        }
        // prevent jsdom based tests from failing because of missing scroll function stubs
        if (!slidesElement.scrollTo) {
            return;
        }
        const targetIndex = endlessScrolling
            ? nextIndex + currentSlidesPerPage + 1
            : (nextIndex + childrenLength) % childrenLength;
        const targetPosition = slidesElement.children[targetIndex].offsetLeft;
        const left = theme.direction === 'rtl' ? -1 * targetPosition : targetPosition;
        slidesElement.scrollTo({
            left,
            behavior: animate ? 'smooth' : 'instant',
        });
        // update index without scroll listener when we are not scrolling
        if (!animate) {
            setExtendedIndex(targetIndex);
        }
    }, [childrenLength, currentSlidesPerPage, endlessScrolling, theme.direction]);
    const handleResize = React.useCallback(() => {
        updateSlidesPerPage();
        updateFadingEdges();
        changeSlide(extendedIndex, false);
    }, [updateSlidesPerPage, updateFadingEdges, changeSlide, extendedIndex]);
    const snappableExtendedIndices = React.useMemo(() => {
        const incrementValue = increment === 'slide' ? 1 : currentSlidesPerPage;
        const snappableSlides = Array.from({
            length: childrenLength / incrementValue,
        }).map((_, index) => index * incrementValue);
        snappableSlides.push(childrenLength - incrementValue);
        snappableSlides.push(childrenLength);
        for (let index = 1; index <= currentSlidesPerPage; index++) {
            const indexOfSlideCopy = 0 - incrementValue * index;
            // Ignore all indices without slides or copies of slides
            if (currentSlidesPerPage + indexOfSlideCopy >= 0) {
                snappableSlides.push(indexOfSlideCopy);
            }
        }
        snappableSlides.sort((a, b) => a - b);
        return snappableSlides;
    }, [currentSlidesPerPage, childrenLength, increment]);
    const slideToNextSlide = React.useCallback(() => {
        changeSlide(extendedIndex + (increment === 'page' ? currentSlidesPerPage : 1));
    }, [changeSlide, extendedIndex, increment, currentSlidesPerPage]);
    const slideToPreviousSlide = React.useCallback(() => {
        changeSlide(extendedIndex - (increment === 'page' ? currentSlidesPerPage : 1));
    }, [changeSlide, extendedIndex, increment, currentSlidesPerPage]);
    const handleScroll = useThrottleCallback(React.useCallback(() => {
        const slidesElement = slides.current;
        if (!slidesElement) {
            return;
        }
        const slidesInner = slidesElement.children;
        if (slidesInner.length === 0) {
            return;
        }
        const scrollLeft = slidesElement.scrollLeft;
        const firstOffsetLeft = slidesInner.item(0).offsetLeft;
        // sometimes the scroll positions we get from the browsers are off by way more than 1px so simply rounding up or down does not work
        const scrollIndex = Array.from(slidesInner).findIndex((element) => Math.abs(element.offsetLeft - firstOffsetLeft - scrollLeft) < 10);
        if (scrollIndex === -1) {
            // not even close to the edge of a tile
            return;
        }
        if (endlessScrolling) {
            const scrollPosition = theme.direction === 'rtl' ? -1 * scrollLeft : scrollLeft;
            const secondSlideStart = slidesInner[1].offsetLeft;
            if (Math.floor(scrollPosition) <= secondSlideStart) {
                // Overflow on the left side
                // when we reach the second slide, so the first one is still visible for fading edges
                setExtendedIndex(childrenLength - currentSlidesPerPage);
                changeSlide(childrenLength - currentSlidesPerPage, false);
                return;
            }
            const lastSlideStart = slidesInner[slidesInner.length - 1].offsetLeft;
            const slidesWidth = slidesElement.getBoundingClientRect().width;
            if (Math.ceil(scrollPosition + slidesWidth) >= lastSlideStart) {
                // Overflow on the right side
                // when we reach the second last slide, so the last one is still visible for fading edges
                setExtendedIndex(0);
                changeSlide(0, false);
                return;
            }
            const endlessScrollIndex = scrollIndex - currentSlidesPerPage - 1;
            if (endlessScrollIndex !== extendedIndex) {
                setExtendedIndex(endlessScrollIndex);
            }
        }
        else {
            if (scrollIndex !== extendedIndex) {
                setExtendedIndex(scrollIndex);
            }
        }
    }, [
        changeSlide,
        childrenLength,
        currentSlidesPerPage,
        endlessScrolling,
        extendedIndex,
        theme.direction,
    ]));
    React.useEffect(() => {
        if (externalHandleChange) {
            externalHandleChange(extendedIndex);
        }
    }, [extendedIndex, externalHandleChange]);
    // this is called when control elements change the index
    const handleChange = React.useCallback((nextIndex, animate) => {
        // find previous snappableIndex counting from nextIndex,
        // i.e. beginning of page containing nextIndex
        const snappableArrayIndex = snappableExtendedIndices.findIndex((value) => value > nextIndex) - 1;
        const snappableTargetIndex = snappableExtendedIndices[snappableArrayIndex] || 0;
        changeSlide(snappableTargetIndex, animate);
    }, [changeSlide, snappableExtendedIndices]);
    // reset scroll position when basic props change
    // biome-ignore lint/correctness/useExhaustiveDependencies: should not update on changes to extendedIndex
    React.useEffect(() => {
        const slidesElement = slides.current;
        if (!slidesElement) {
            return;
        }
        changeSlide(extendedIndex, false);
    }, [changeSlide, childrenLength, currentSlidesPerPage, endlessScrolling]);
    // When endlessScrolling is set scroll to starting position.
    React.useEffect(() => {
        if (endlessScrolling) {
            changeSlide(0, false);
            setExtendedIndex(0);
        }
    }, [changeSlide, endlessScrolling]);
    // Add and remove resize listeners
    React.useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);
    React.useEffect(() => {
        updateSlidesPerPage();
    }, [updateSlidesPerPage, slidesPerPage]);
    React.useEffect(() => {
        updateFadingEdges();
    }, [updateFadingEdges, fadingEdges]);
    // When react moves around galleries in the DOM, browsers sometimes reset their scrollLeft to 0.
    // Reset the scroll position to the appropriate value.
    React.useEffect(() => {
        const slidesElement = slides.current;
        if (!slidesElement) {
            return;
        }
        const scrollLeft = slidesElement.scrollLeft;
        if ((endlessScrolling || extendedIndex !== 0) && scrollLeft === 0) {
            changeSlide(extendedIndex, false);
        }
    });
    // fix index if slide has been deleted
    React.useEffect(() => {
        const newExtendedIndex = endlessScrolling
            ? (extendedIndex + childrenLength) % childrenLength
            : Math.min(extendedIndex, childrenLength - currentSlidesPerPage);
        if (extendedIndex !== newExtendedIndex) {
            changeSlide(newExtendedIndex, false);
        }
    }, [
        extendedIndex,
        childrenLength,
        endlessScrolling,
        currentSlidesPerPage,
        changeSlide,
    ]);
    const index = React.useMemo(() => (extendedIndex + childrenLength) % childrenLength, [extendedIndex, childrenLength]);
    // accessibleExtendedIndices,
    // i.e. those that are fully visible
    const accessibleExtendedIndices = React.useMemo(() => {
        const firstAccessibleIndex = endlessScrolling
            ? extendedIndex
            : Math.min(extendedIndex, childrenLength - currentSlidesPerPage);
        return Array.from({ length: currentSlidesPerPage }).map((_, index) => firstAccessibleIndex + index);
    }, [endlessScrolling, extendedIndex, childrenLength, currentSlidesPerPage]);
    // visibleIndices include accessibleExtendedIndices
    // slides of accessible duplicated slides.
    //
    // extendedIndex: -2 -1 0 1 2 3 4 5 6
    // index:          3  4 0 1 2 3 4 0 1
    //
    // e.g. if slide with (extended) index 5 is in the view, slides 0 is visible.
    // The slide component will render slide 5 (copy of slide 0) visible, too.
    const visibleIndices = React.useMemo(() => Array.from({ length: currentSlidesPerPage }).map((_, index) => (extendedIndex + index + childrenLength) % childrenLength), [currentSlidesPerPage, extendedIndex, childrenLength]);
    const galleryContextValue = React.useMemo(() => {
        return {
            index,
            extendedIndex,
            endlessScrolling: endlessScrolling || false,
            triggerChange: handleChange,
            triggerPrevious: slideToPreviousSlide,
            triggerNext: slideToNextSlide,
            getSlideId: (index) => `${id}__slide-${index}`,
            disableScrollSnap: (disabled = true) => setDisableSnap(disabled),
            slidesPerPage: currentSlidesPerPage,
            fadingEdges: currentFadingEdges,
            length: childrenLength,
            id: id,
            accessibleExtendedIndices,
            visibleIndices,
            snappableExtendedIndices,
            slidesRef: slides,
        };
    }, [
        accessibleExtendedIndices,
        childrenLength,
        currentFadingEdges,
        currentSlidesPerPage,
        endlessScrolling,
        extendedIndex,
        handleChange,
        id,
        index,
        slideToNextSlide,
        slideToPreviousSlide,
        snappableExtendedIndices,
        visibleIndices,
        slides,
    ]);
    const slideData = React.useMemo(() => {
        const leadingSlides = [];
        const slides = [];
        const trailingSlides = [];
        React.Children.toArray(children)
            .filter(React.isValidElement)
            .forEach((child, index) => {
            slides.push({
                key: child.key,
                value: {
                    index,
                },
                child,
            });
            // Only render copies of slides when endlessScrolling is enabled and
            // we have more children than can be show on a single page
            if (endlessScrolling && childrenLength > currentSlidesPerPage) {
                if (index < currentSlidesPerPage + 1) {
                    const id = childrenLength + index;
                    trailingSlides.push({
                        key: `${child.key}-${id}`,
                        value: {
                            index: index,
                            id: id,
                        },
                        child,
                    });
                }
                if (index > childrenLength - currentSlidesPerPage - 2) {
                    const id = index - childrenLength;
                    leadingSlides.push({
                        key: `${child.key}-${id}`,
                        value: {
                            index: index,
                            id: id,
                        },
                        child,
                    });
                }
            }
        });
        return [...leadingSlides, ...slides, ...trailingSlides];
    }, [children, childrenLength, currentSlidesPerPage, endlessScrolling]);
    const mediaGalleryContextValue = React.useMemo(() => {
        const mediaGalleryState = {
            // for now we just add the values needed for the lentils
            activeIndex: index,
            slideLength: childrenLength,
            slideKeys: React.Children.toArray(children)
                .filter(React.isValidElement)
                .map((child) => child.key),
            // the following values are not maintained, but they are not needed for the lentils
            lastIndex: NaN,
            animate: false,
            dragStartPosition: NaN,
            dragStartTime: NaN,
            dragEnabled: false,
            slidePosition: NaN,
        };
        const dispatch = (action) => {
            // we currently only handle the lentils' SLIDE_CHANGE action
            if (action.type === 'SLIDE_CHANGE') {
                handleChange(action.index);
            }
        };
        return {
            state: mediaGalleryState,
            dispatch,
            getState: () => mediaGalleryState,
        };
    }, [children, childrenLength, handleChange, index]);
    return (React.createElement(GalleryContext.Provider, { value: galleryContextValue },
        React.createElement(MediaGalleryContext.Provider, { value: mediaGalleryContextValue },
            React.createElement(StyledCarousel, Object.assign({ id: id, tabIndex: -1, "aria-label": ariaLabel, role: "region", "aria-roledescription": "carousel", "aria-labelledby": ariaLabelledby }, commonProps),
                topElements,
                React.createElement(ChildContainerTabIndexManager, { childContainerSelector: '[data-accessible]', activeChildContainerSelector: '[data-accessible="true"]' },
                    React.createElement(StyledSlides, { ref: slides, onScroll: handleScroll, "aria-atomic": "false", "aria-live": "polite", slidesPerPage: currentSlidesPerPage, fadingEdges: currentFadingEdges, gutter: currentGutter, tabIndex: 0, disableSnap: disableSnap }, slideData.map(({ key, value, child }) => (React.createElement(GallerySlideContext.Provider, { key: key, value: value }, child))))),
                bottomElements))));
};
